import MapsTable from "./MapsTable";
import "../Maps/Maps.css";
import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserData,
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";
import { useEffect, useState } from "react";
import { MapsEndpointLogic } from "../../../../logic/DataCollection/Unifier/mapsEndpointLogic";
import SimpleBackdrop from "../../../BackDrop/BackDrop";
import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  Button,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSearchParams } from "react-router-dom";
import {
  setIsValidPayloadDetails,
  setMapDetails,
  setSelectedDestinationTable,
  setSelectedDestinationTableID,
  setSelectedDestinationTableIDMaps,
  setSelectedInterface,
  setSelectedInterfaceID,
} from "../../../../features/mapsSlice";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import Page404 from "../../../Page404/Page403";

const Maps = () => {
  const dispatch = useDispatch();
  const response = useSelector(selectUserData);
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const [loading, setLoading] = useState(false);
  const [, setSearch] = useState(false);
  const [dataShow, setDataShow] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const mapsData = useSelector((state: any) => state.maps.mapsData);
  const interfaceData = useSelector((state: any) => state.maps.interfaceData);
  const destinationTablesData = useSelector(
    (state: any) => state.maps.destinationTablesData,
  );
  const selectedInterface = useSelector(
    (state: any) => state.maps.selectedInterface,
  );
  const selectedInterfaceID = useSelector(
    (state: any) => state.maps.selectedInterfaceID,
  );
  const selectedDestinationTable = useSelector(
    (state: any) => state.maps.selectedDestinationTable,
  );
  const selectedDestinationTableID = useSelector(
    (state: any) => state.maps.selectedDestinationTableID,
  );
  const [interfaceLoading, setInterfaceLoading] = useState(true);
  const interfaceFromURL = searchParams.get("interface");
  const interfaceIDFromURL = searchParams.get("interfaceID");
  const apiCalledRef = useRef(false);
  useEffect(() => {
    dispatch(setMapDetails([]));
    dispatch(setSelectedDestinationTableIDMaps(null));
    dispatch(setIsValidPayloadDetails(false));

    apiCalledRef.current = false;
  }, [dispatch]);

  useEffect(() => {
    if (searchParams.get("mapId")) {
      searchParams.delete("mapId");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    return () => {
      // Reset when component unmounts (navigating away)
      dispatch(setSelectedInterface(null));
      dispatch(setSelectedInterfaceID(null));
      dispatch(setSelectedDestinationTable(null));
      dispatch(setSelectedDestinationTableID(null));
      apiCalledRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Modify this useEffect to track when interface data is loaded
  useEffect(() => {
    const fetchData = async () => {
      setInterfaceLoading(true);
      try {
        await Promise.all([
          MapsEndpointLogic.getInterface(oauthTokenHeader),
          MapsEndpointLogic.getDestinationTables(oauthTokenHeader),
        ]);
      } catch (error: any) {
        dispatch(
          setDisplayErrorSnackBar({
            message: error?.parsedText || "Unknown error occurred",
            type: "error",
          }),
        );
      } finally {
        setInterfaceLoading(false);
      }
    };
    fetchData();
  }, [dispatch, oauthTokenHeader]);
  // Add new useEffect to handle URL params and trigger initial data load
  useEffect(() => {
    if (
      interfaceFromURL &&
      interfaceIDFromURL &&
      interfaceData?.length > 0 &&
      !apiCalledRef.current
    ) {
      dispatch(setSelectedInterface(interfaceFromURL));
      dispatch(setSelectedInterfaceID(interfaceIDFromURL));
      setSearch(true);
      setDataShow(true);

      const fetchMapsData = async () => {
        setLoading(true);
        apiCalledRef.current = true;
        try {
          await MapsEndpointLogic.getMaps(
            oauthTokenHeader,
            interfaceIDFromURL,
            dispatch,
          );
          setDataLoaded(true);
        } catch (error: any) {
          dispatch(
            setDisplayErrorSnackBar({
              message:
                error?.parsedText ||
                "Error while fetching data. Please try again later.",
              type: "error",
            }),
          );
          apiCalledRef.current = false;
        } finally {
          setLoading(false);
        }
      };
      fetchMapsData();
    }
    // eslint-disable-next-line
  }, [interfaceData, oauthTokenHeader]);

  // REMOVED the automatic API call useEffect - this was causing the issue
  // The API will now only be called when handleSearch is explicitly called

  useEffect(() => {
    const selectedDestinationTableFromURL =
      searchParams.get("destinationTable");
    const selectedDestinationTableIDFromURL =
      searchParams.get("destinationTableID");
    if (selectedDestinationTableFromURL && selectedDestinationTableIDFromURL) {
      dispatch(setSelectedDestinationTable(selectedDestinationTableFromURL));
      dispatch(
        setSelectedDestinationTableID(selectedDestinationTableIDFromURL),
      );
    }
    // eslint-disable-next-line
  }, [searchParams]);

  // Update handleSearch function to be the ONLY place where API is called
  const handleSearch = async (fromReload = false) => {
    const interfaceID =
      selectedInterfaceID ||
      searchParams.get("interfaceID") ||
      interfaceIDFromURL;
    const interfaceName =
      selectedInterface || searchParams.get("interface") || interfaceFromURL;

    if (interfaceID && interfaceName) {
      setSearch(true);
      setDataShow(true);
      setLoading(true);
      setDataLoaded(false);
      apiCalledRef.current = true;
      if (!fromReload) {
        dispatch(setSelectedInterfaceID(interfaceID));
        dispatch(setSelectedInterface(interfaceName));
        // Preserve existing params
        const updatedParams = new URLSearchParams(window.location.search);
        updatedParams.set("interface", interfaceName);
        updatedParams.set("interfaceID", interfaceID);
        if (selectedDestinationTable && selectedDestinationTableID) {
          updatedParams.set("destinationTable", selectedDestinationTable);
          updatedParams.set("destinationTableID", selectedDestinationTableID);
        }
        setSearchParams(updatedParams, { replace: true });
      }

      // API call happens here - ONLY when button is clicked
      try {
        await MapsEndpointLogic.getMaps(
          oauthTokenHeader,
          interfaceID,
          dispatch,
        );
        setDataLoaded(true);
      } catch (error: any) {
        dispatch(
          setDisplayErrorSnackBar({
            message:
              error?.parsedText ||
              "Error while fetching data. Please try again later.",
            type: "error",
          }),
        );
        apiCalledRef.current = false;
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSelectInterface = (_: unknown, newValue: any) => {
    apiCalledRef.current = false;
    if (!newValue) {
      dispatch(setSelectedInterface(null));
      dispatch(setSelectedInterfaceID(null));
      setDataShow(false);
      setDataLoaded(false);
      const updatedParams = new URLSearchParams(window.location.search);
      updatedParams.delete("interface");
      updatedParams.delete("interfaceID");
      setSearchParams(updatedParams, { replace: true });
    } else {
      dispatch(setSelectedInterface(newValue?.label));
      dispatch(setSelectedInterfaceID(newValue?.id));
      const updatedParams = new URLSearchParams(window.location.search);
      updatedParams.set("interface", newValue?.label);
      updatedParams.set("interfaceID", newValue?.id);
      setSearchParams(updatedParams);

      // DO NOT call API here - only update the state
      // User must click "Get Data" button to trigger API call
    }
  };

  const handleSelectDestinationTable = (_: unknown, newValue: any) => {
    const updatedParams = new URLSearchParams(window.location.search);
    if (!newValue) {
      dispatch(setSelectedDestinationTable(null));
      dispatch(setSelectedDestinationTableID(null));
      updatedParams.delete("destinationTable");
      updatedParams.delete("destinationTableID");
    } else {
      dispatch(setSelectedDestinationTable(newValue?.label));
      dispatch(setSelectedDestinationTableID(newValue?.id));
      // Preserve interface params while updating destination table params
      if (selectedInterface && selectedInterfaceID) {
        updatedParams.set("interface", selectedInterface);
        updatedParams.set("interfaceID", selectedInterfaceID);
      }
      updatedParams.set("destinationTable", newValue?.label);
      updatedParams.set("destinationTableID", newValue?.id);
    }
    setSearchParams(updatedParams, { replace: true });
  };

  const LISTBOX_PADDING = 8;
  const renderRow = (props: ListChildComponentProps) => {
    const { data, index, style } = props;
    const option = data[index];
    return React.cloneElement(option, {
      style: {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING,
      },
    });
  };

  const VirtualizedListboxComponent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLElement>
  >(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemCount = Array.isArray(children) ? children.length : 0;
    const itemSize = 36; // item height in px, adjust to your needs
    const height = Math.min(8, itemCount) * itemSize + 2 * LISTBOX_PADDING;

    return (
      <div ref={ref} {...other}>
        <FixedSizeList
          height={height}
          width="100%"
          itemSize={itemSize}
          itemCount={itemCount}
          overscanCount={5}
          itemData={children}
          style={{ padding: LISTBOX_PADDING }}
        >
          {renderRow}
        </FixedSizeList>
      </div>
    );
  });

  const selectedInterfaceValue = selectedInterface
    ? interfaceData?.find(
        (item: any) =>
          item.affiliate_system_interface_name === selectedInterface,
      )
      ? {
          label: selectedInterface,
          id: interfaceData.find(
            (item: any) =>
              item.affiliate_system_interface_name === selectedInterface,
          ).id,
        }
      : null
    : null;

  const selectedDestinationTableValue = selectedDestinationTable
    ? destinationTablesData?.find(
        (item: any) => item.destination_table === selectedDestinationTable,
      )
      ? {
          label: selectedDestinationTable,
          id: destinationTablesData.find(
            (item: any) => item.destination_table === selectedDestinationTable,
          ).id,
        }
      : null
    : null;

  // Filter mapsData based on selected destination table
  const filteredMapsData = React.useMemo(() => {
    if (!selectedDestinationTableID || !mapsData) {
      return mapsData;
    }
    return mapsData.filter(
      (item: any) =>
        item.destination_table_id === parseInt(selectedDestinationTableID),
    );
  }, [mapsData, selectedDestinationTableID]);

  const hasViewUnifierMaps = CheckPermissions(
    response?.data?.products?.data_collection,
    "unifier",
    "view_maps",
  );

  return (
    <>
      {/* <MapsGetData setLoading={setLoading} /> */}
      {hasViewUnifierMaps ? (
        <>
          <Paper elevation={0} sx={{ padding: "12px 16px" }}>
            <Grid container alignItems="center" columnSpacing={3}>
              <Grid size={6}>
                <Typography variant="h3" sx={{ fontSize: "24px" }}>
                  Unifier Maps
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "14px" }}>
                  The series of linkages between a cell/value on the raw table
                  to the cell/value on the destination table.
                </Typography>
              </Grid>
              <Grid size={6}>
                <Stack
                  direction="row"
                  alignItems="flex-end"
                  justifyContent={"flex-end"}
                  gap={2}
                >
                  <Autocomplete
                    sx={{ width: "100%" }}
                    options={
                      interfaceData?.map((item: any) => ({
                        label: item.affiliate_system_interface_name,
                        id: item.id,
                      })) || []
                    }
                    loading={interfaceLoading}
                    ListboxComponent={VirtualizedListboxComponent}
                    value={selectedInterfaceValue}
                    onChange={handleSelectInterface}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          interfaceLoading
                            ? "Loading..."
                            : "Choose from the list"
                        }
                        label="Interface"
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ marginLeft: "12px" }} />
                            </InputAdornment>
                          ),
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                  />
                  {/* {dataShow && dataLoaded && mapsData?.length > 0 && ( */}
                  <Autocomplete
                    sx={{ width: "100%" }}
                    options={
                      destinationTablesData
                        ?.filter(
                          (item: any) =>
                            item.destination_table !==
                            "daily_brand_campaign_performance",
                        )
                        .map((item: any) => ({
                          label: item.destination_table,
                          id: item.id,
                        })) || []
                    }
                    ListboxComponent={VirtualizedListboxComponent}
                    value={selectedDestinationTableValue}
                    onChange={handleSelectDestinationTable}
                    clearOnBlur={false}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Filter by destination table"
                        label="Destination Table Filter"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ marginLeft: "12px" }} />
                            </InputAdornment>
                          ),
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                  />
                  {/* )} */}
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={!selectedInterface}
                    onClick={() => handleSearch(false)}
                    sx={{
                      padding: "8px 22px",
                      width: "30%",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Get Data
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
          {dataShow && (
            <>
              {loading ? (
                <SimpleBackdrop />
              ) : dataLoaded && mapsData?.length > 0 ? (
                <>
                  <MapsTable mapsData={filteredMapsData} />
                  {filteredMapsData?.length === 0 &&
                    selectedDestinationTable && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 3, textAlign: "center", color: "#888" }}
                      >
                        No maps found for the selected destination table "
                        {selectedDestinationTable}". Try clearing the
                        destination table filter to see all maps.
                      </Typography>
                    )}
                  {selectedDestinationTable && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontSize: "12px",
                        mt: 2,
                        textAlign: "center",
                      }}
                    >
                      Showing {filteredMapsData?.length || 0} of{" "}
                      {mapsData?.length || 0} maps
                    </Typography>
                  )}
                </>
              ) : dataLoaded && mapsData?.length <= 0 ? (
                <Typography
                  variant="body2"
                  sx={{ mt: 3, textAlign: "center", color: "#888" }}
                >
                  No data available. Please select an interface and click "Get
                  Data".
                </Typography>
              ) : null}
            </>
          )}
        </>
      ) : (
        <Page404 />
      )}
    </>
  );
};

export default Maps;
